import type { DeliveryMode, PlanTier } from '@signbento/contracts'
import { RELEASE_CONFIG } from './config/release-config.js'
import { err, ok, type Result } from './result.js'
import { MS_PER_DAY } from './time.js'

/**
 * Tier limits and validation — PRD §2.2, §6.1, §14.3, §14.6.
 * Limits are domain rules with tests; UI must never re-implement them.
 */

export function externalSignerLimit(tier: PlanTier): number {
  return tier === 'FREE'
    ? RELEASE_CONFIG.free_external_signers.value
    : RELEASE_CONFIG.lifetime_external_signers.value
}

/**
 * The tier whose request limits one request is held to — DEV-006 (Hamza, 17 Oct 2026: "first flow
 * should be full managed that allows paid options, multiple signers etc").
 *
 * A managed request is paid for by a Lifetime credit or, on Free, by the account's one trial, so a
 * Free managed request *is* the trial: it gets Lifetime's signer, copy and signing-window limits.
 * Everything that belongs to the account rather than the request (active-request capacity,
 * attachment requests, retention, request operations) keeps the account's own tier. Whether the
 * trial is still available is decided separately, in the same transaction (`delivery_trials`).
 */
export function requestLimitTier(account: PlanTier, delivery: DeliveryMode): PlanTier {
  return account === 'LIFETIME' || delivery === 'MANAGED' ? 'LIFETIME' : 'FREE'
}

export function activeRequestLimit(tier: PlanTier): number {
  return tier === 'FREE'
    ? RELEASE_CONFIG.free_active_requests.value
    : RELEASE_CONFIG.lifetime_active_requests.value
}

export interface ExpiryBounds {
  readonly min_days: number
  readonly default_days: number
  readonly max_days: number
}

export function expiryBounds(tier: PlanTier): ExpiryBounds {
  if (tier === 'FREE') {
    const fixed = RELEASE_CONFIG.free_signing_days.value
    return { min_days: fixed, default_days: fixed, max_days: fixed }
  }
  return {
    // The PRD fixes the Lifetime default (30) and maximum (90); the smallest
    // positive duration is a schema-level lower bound, not a commercial value.
    min_days: 1,
    default_days: RELEASE_CONFIG.lifetime_default_signing_days.value,
    max_days: RELEASE_CONFIG.lifetime_max_signing_days.value,
  }
}

export interface ParticipantSpec {
  readonly kind: 'signer' | 'cc'
  /** Required for signing; CC recipients are optional by definition. */
  readonly required?: boolean
  /** True when this participant is the verified sender adopting their own signature. */
  readonly is_sender?: boolean
}

/** External required signers exclude the sender and every CC recipient. */
export function countExternalRequiredSigners(participants: readonly ParticipantSpec[]): number {
  return participants.filter(
    (participant) =>
      participant.kind === 'signer' &&
      participant.is_sender !== true &&
      participant.required !== false,
  ).length
}

export function validateExternalSignerCount(
  tier: PlanTier,
  participants: readonly ParticipantSpec[],
): Result<number> {
  const count = countExternalRequiredSigners(participants)
  const limit = externalSignerLimit(tier)
  if (count > limit) {
    return err(tier === 'FREE' ? 'FREE_SIGNER_LIMIT' : 'LIMIT_EXCEEDED')
  }
  return ok(count)
}

export function validateCcCount(tier: PlanTier, count: number): Result<number> {
  if (tier !== 'LIFETIME') return err('OP_FORBIDDEN')
  if (!Number.isInteger(count) || count < 0) return err('OP_INVALID_PAYLOAD')
  return count <= RELEASE_CONFIG.cc_recipient_cap.value ? ok(count) : err('LIMIT_EXCEEDED')
}

/** Active slots = ACTIVE + AWAITING_FINALIZATION + unexpired PREPARING reservations. */
export function validateActiveCount(tier: PlanTier, heldSlots: number): Result<number> {
  const limit = activeRequestLimit(tier)
  if (heldSlots >= limit) {
    return err(tier === 'FREE' ? 'FREE_ACTIVE_LIMIT' : 'LIMIT_EXCEEDED')
  }
  return ok(heldSlots)
}

export function validateExpiryDays(tier: PlanTier, days: number): Result<number> {
  if (!Number.isInteger(days) || days <= 0) return err('OP_INVALID_PAYLOAD')
  const bounds = expiryBounds(tier)
  if (days < bounds.min_days || days > bounds.max_days) return err('OP_INVALID_PAYLOAD')
  return ok(days)
}

/** Free expiry is a fixed elapsed seven days from server activation. */
export function expiryDeadline(tier: PlanTier, activatedAtMs: number): number {
  return activatedAtMs + expiryBounds(tier).default_days * MS_PER_DAY
}

export interface SourceInput {
  readonly bytes: number
  readonly pages: number
  /** Decoded pixels for the current image being processed (sequential pipeline). */
  readonly decoded_pixels?: number
  /** Cumulative decoded pixels for the import. */
  readonly total_decoded_pixels?: number
}

export function validateSourceInput(input: SourceInput): Result<void> {
  const maxBytes = RELEASE_CONFIG.source_max_bytes.value
  const maxPages = RELEASE_CONFIG.source_max_pages.value
  if (input.bytes > maxBytes) return err('OP_PDF_TOO_LARGE')
  if (input.pages > maxPages) return err('OP_PDF_TOO_MANY_PAGES')
  if (
    input.decoded_pixels !== undefined &&
    input.decoded_pixels > RELEASE_CONFIG.image_max_decoded_pixels.value
  ) {
    return err('OP_PDF_IMAGE_PIXEL_LIMIT')
  }
  if (
    input.total_decoded_pixels !== undefined &&
    input.total_decoded_pixels > RELEASE_CONFIG.import_max_decoded_pixels.value
  ) {
    return err('OP_PDF_IMAGE_PIXEL_LIMIT')
  }
  return ok(undefined)
}

export interface MessageTextInput {
  readonly subject?: string
  readonly message?: string
  readonly decline_reason?: string
}

/** Unicode scalar values, not UTF-16 units (§4.6); overflow is rejected, never truncated. */
export function unicodeScalarLength(value: string): number {
  return [...value].length
}

export function validateMessageText(input: MessageTextInput): Result<void> {
  if (
    input.subject !== undefined &&
    unicodeScalarLength(input.subject) > RELEASE_CONFIG.subject_max_scalars.value
  ) {
    return err('OP_INVALID_PAYLOAD')
  }
  if (
    input.message !== undefined &&
    unicodeScalarLength(input.message) > RELEASE_CONFIG.message_max_scalars.value
  ) {
    return err('OP_INVALID_PAYLOAD')
  }
  if (
    input.decline_reason !== undefined &&
    unicodeScalarLength(input.decline_reason) > RELEASE_CONFIG.decline_reason_max_scalars.value
  ) {
    return err('OP_INVALID_PAYLOAD')
  }
  return ok(undefined)
}
